import { useState } from 'react';
import { Link } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta, SITE_URL } from '@/components/PageMeta';
import { ROUTES } from '@/config/routes';
import { useSaasStore } from '@/stores/saas-store';
import {
  CLUB_PRO_ANNUAL_TTC_CENTS,
  CLUB_PRO_MONTHLY_TTC_CENTS,
  COMMISSION_FIXED_FEE_CENTS,
  COMMISSION_MIN_FEE_CENTS,
  COMMISSION_MONTHLY_LIMIT,
  COMMISSION_RATE_BPS,
  COMMISSION_YEARLY_LIMIT,
  FORFAIT_ENABLED,
  JOUEUR_PLUS_ANNUAL_TTC_CENTS,
  JOUEUR_PLUS_MONTHLY_TTC_CENTS,
  ORGA_FORFAIT_ANNUAL_TTC_CENTS,
  ORGA_FORFAIT_MONTHLY_TTC_CENTS,
} from '@shared/constants';
import { ArrowRight, Calendar, Check, ChevronDown, Clock, Tag, X } from 'lucide-react';

type Billing = 'mensuel' | 'annuel';

// Libellés commission dérivés des constantes partagées (single source of truth).
const fmtEur = (cents: number) => `${(cents / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €`;
const COMMISSION_LABEL = `${COMMISSION_RATE_BPS / 100} % + ${fmtEur(COMMISSION_FIXED_FEE_CENTS)}`;
const COMMISSION_FULL_LABEL = `${COMMISSION_LABEL}, min ${fmtEur(COMMISSION_MIN_FEE_CENTS)}`;

const COMMISSION_LIMITS_LABEL = `Jusqu'à ${COMMISSION_MONTHLY_LIMIT} concours payants/mois (${COMMISSION_YEARLY_LIMIT}/an)`;

const plans = [
  {
    audience: 'Joueurs',
    description: 'Pour les joueurs qui participent aux concours.',
    tiers: [
      {
        name: 'Free',
        priceLabel: '',
        tagline: 'Pour commencer',
        price: { mensuel: 0, annuel: 0 },
        features: [
          'Profil joueur complet',
          'Inscription aux concours',
          'Historique personnel des résultats',
          'Recherche de concours à proximité',
        ],
        // notIncluded: ['Statistiques avancées', 'Export des résultats'],
        notIncluded: [],
        cta: 'Créer mon compte',
        highlighted: false,
      },
      // {
      //   name: 'Plus',
      //   priceLabel: '',
      //   tagline: 'Pour les compétiteurs',
      //   price: { mensuel: JOUEUR_PLUS_MONTHLY_TTC_CENTS / 100, annuel: JOUEUR_PLUS_ANNUAL_TTC_CENTS / 100 },
      //   features: [
      //     'Tout le plan Free',
      //     'Statistiques avancées et graphiques',
      //     'Export CSV de vos résultats',
      //     'Historique complet toutes saisons',
      //     'Classement détaillé par catégorie',
      //   ],
      //   notIncluded: [],
      //   cta: 'Passer au Plus',
      //   highlighted: true,
      // },
    ],
  },
  {
    audience: 'Clubs',
    description: 'Pour les clubs qui gèrent leurs adhérents.',
    tiers: [
      {
        name: 'Gratuit',
        priceLabel: '',
        tagline: 'Pour les petits clubs',
        price: { mensuel: 0, annuel: 0 },
        features: ['Page club publique', 'Annuaire des membres', 'Agenda des concours du club'],
        notIncluded: ['Informations des licences'],
        cta: 'Créer mon club',
        highlighted: false,
      },
      {
        name: 'Pro',
        priceLabel: '',
        tagline: 'Pour les clubs structurés',
        price: { mensuel: CLUB_PRO_MONTHLY_TTC_CENTS / 100, annuel: CLUB_PRO_ANNUAL_TTC_CENTS / 100 },
        features: [
          'Tout le plan Gratuit',
          'Gestion des licences',
          'Statistiques du club',
          'Exports CSV avancés',
          'Blog du club',
        ],
        notIncluded: [],
        cta: 'Passer au Pro',
        highlighted: true,
      },
    ],
  },
  {
    audience: 'Organisateurs',
    description: 'Pour les comités, clubs et particuliers qui organisent des concours.',
    tiers: [
      {
        name: 'Commission',
        tagline: 'Sans abonnement fixe',
        price: { mensuel: 0, annuel: 0 },
        priceLabel: COMMISSION_LABEL,
        features: [
          'Concours gratuits = 0 € (aucun frais)',
          `Commission : ${COMMISSION_FULL_LABEL}/inscription`,
          COMMISSION_LIMITS_LABEL,
          'Inscriptions en ligne',
          'Paiement sécurisé via Stripe',
          'Gestion du règlement et export FFPJP',
          'Page publique du concours',
          'Support par email',
        ],
        notIncluded: [],
        cta: 'Créer mon premier concours',
        highlighted: false,
      },
      ...(FORFAIT_ENABLED
        ? [
            {
              name: 'Forfait',
              tagline: '0 % de commission — concours payants illimités',
              // Le toggle global Mensuel / Annuel pilote le prix affiché
              price: {
                mensuel: ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100,
                annuel: ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100,
              },
              priceLabel: '',
              features: [
                '0 % de commission sur les inscriptions',
                'Concours payants illimités',
                "Blog de l'organisateur",
                'Inscriptions en ligne',
                'Paiement sécurisé via Stripe',
                'Gestion du règlement et export FFPJP',
                'Page publique du concours',
                'Support prioritaire',
              ],
              notIncluded: [],
              cta: 'Passer au Forfait',
              highlighted: true,
            },
          ]
        : []),
    ],
  },
];

// const comparatifLignes = [
//   { feature: 'Profil complet', free: true, plus: true },
//   { feature: 'Inscription aux concours', free: true, plus: true },
//   { feature: 'Historique personnel', free: 'Saison en cours', plus: 'Toutes saisons' },
//   { feature: 'Statistiques avancées', free: false, plus: true },
//   { feature: 'Export CSV', free: false, plus: true },
//   { feature: 'Support', free: 'Communauté', plus: 'Email prioritaire' },
// ];

const faq = [
  {
    q: 'Comment fonctionne la commission pour les organisateurs ?',
    a: (
      <>
        Pour chaque inscription <strong>payante</strong>, BoulesConnect prélève une commission de{' '}
        <strong>{COMMISSION_FULL_LABEL} par inscription</strong> (TVA incluse). Concrètement : un concours à{' '}
        <strong>10 €/équipe</strong> revient à <strong>{fmtEur(COMMISSION_MIN_FEE_CENTS)}/équipe</strong> (le minimum),
        soit environ 0,25 €/joueur en triplette ; un concours à <strong>50 €/équipe</strong> revient à{' '}
        <strong>1,30 €</strong>. Aucun abonnement mensuel, aucun frais fixe : vous ne payez que lorsque vos concours
        génèrent du chiffre d'affaires. <strong>Les concours gratuits sont totalement gratuits</strong> : ni commission
        BoulesConnect, ni frais Stripe. Le plan Commission est limité à {COMMISSION_MONTHLY_LIMIT} concours payants par
        mois et {COMMISSION_YEARLY_LIMIT} par an ; au-delà, passez au Forfait.
      </>
    ),
  },
  {
    q: 'Quelle est la différence entre Commission et Forfait ?',
    a: (
      <>
        Le plan <strong>Commission</strong> est gratuit : vous ne payez que {COMMISSION_FULL_LABEL} sur les inscriptions
        payantes, dans la limite de {COMMISSION_MONTHLY_LIMIT} concours payants par mois ({COMMISSION_YEARLY_LIMIT}/an).
        Le plan <strong>Forfait</strong> ({ORGA_FORFAIT_MONTHLY_TTC_CENTS / 100}€ TTC/mois ou{' '}
        {ORGA_FORFAIT_ANNUAL_TTC_CENTS / 100}€ TTC/an) supprime toute commission et offre des concours payants
        illimités. Les concours gratuits restent sans frais dans les deux cas.
      </>
    ),
  },
  {
    q: 'Les frais bancaires Stripe sont-ils inclus dans la commission ?',
    a: (
      <>
        Non. Les frais de traitement Stripe (consultables sur{' '}
        <a
          href='https://stripe.com/fr/pricing'
          target='_blank'
          rel='noopener noreferrer'
          className='text-blue-600 underline hover:text-blue-800'
        >
          stripe.com/fr/pricing
        </a>
        ) sont prélevés par Stripe <strong>directement sur le compte de l'organisateur</strong> à chaque transaction, en
        plus de la commission BoulesConnect ({COMMISSION_FULL_LABEL}). Ce sont des frais bancaires standards pour tout
        paiement en ligne sécurisé.
      </>
    ),
  },
  {
    q: 'Puis-je résilier mon abonnement à tout moment ?',
    a: (
      <>
        Oui. Vous pouvez résilier votre abonnement depuis votre espace personnel. En cas de résiliation, votre
        abonnement reste actif jusqu&apos;à la fin de la période facturée.
      </>
    ),
  },
  {
    q: "Les tarifs sont-ils susceptibles d'évoluer ?",
    a: (
      <>
        Oui. En cas de hausse de tarif, vous êtes notifié par email au moins 30 jours avant la date d'effet. Vous pouvez
        résilier sans frais avant l'entrée en vigueur du nouveau tarif ; à défaut, la modification s'applique aux
        périodes suivantes.
      </>
    ),
  },
];

// ─── JSON-LD ──────────────────────────────────────────────────────────────
const eur = (cents: number) => (cents / 100).toFixed(2);

// Les offres JSON-LD doivent refléter EXACTEMENT les plans réellement proposés dans l'UI :
// Joueur Plus est gaté par `playerPlusEnabled`, le Forfait par `FORFAIT_ENABLED`.
// Ne pas annoncer à Google un plan masqué côté site.
const buildSoftwareJsonLd = (playerPlusEnabled: boolean) => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'BoulesConnect',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: `${SITE_URL}/tarifs`,
  description:
    'Plateforme SaaS de gestion de concours de pétanque, jeu provençal et boule lyonnaise : inscriptions en ligne, paiements sécurisés, gestion des clubs et exports compatibles FFPJP.',
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'EUR',
    lowPrice: '0',
    offers: [
      { '@type': 'Offer', name: 'Joueur — Gratuit', price: '0', priceCurrency: 'EUR' },
      ...(playerPlusEnabled
        ? [
            {
              '@type': 'Offer',
              name: 'Joueur Plus (mensuel)',
              price: eur(JOUEUR_PLUS_MONTHLY_TTC_CENTS),
              priceCurrency: 'EUR',
            },
            {
              '@type': 'Offer',
              name: 'Joueur Plus (annuel)',
              price: eur(JOUEUR_PLUS_ANNUAL_TTC_CENTS),
              priceCurrency: 'EUR',
            },
          ]
        : []),
      {
        '@type': 'Offer',
        name: 'Organisateur — Commission',
        description: `Gratuit, commission de ${COMMISSION_FULL_LABEL} TTC par inscription payante.`,
        price: '0',
        priceCurrency: 'EUR',
      },
      ...(FORFAIT_ENABLED
        ? [
            {
              '@type': 'Offer',
              name: 'Organisateur — Forfait (mensuel)',
              price: eur(ORGA_FORFAIT_MONTHLY_TTC_CENTS),
              priceCurrency: 'EUR',
            },
            {
              '@type': 'Offer',
              name: 'Organisateur — Forfait (annuel)',
              price: eur(ORGA_FORFAIT_ANNUAL_TTC_CENTS),
              priceCurrency: 'EUR',
            },
          ]
        : []),
      { '@type': 'Offer', name: 'Club Pro (mensuel)', price: eur(CLUB_PRO_MONTHLY_TTC_CENTS), priceCurrency: 'EUR' },
      { '@type': 'Offer', name: 'Club Pro (annuel)', price: eur(CLUB_PRO_ANNUAL_TTC_CENTS), priceCurrency: 'EUR' },
    ],
  },
});

// Réponses en texte brut (les réponses affichées sont en JSX riche).
const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      q: 'Comment fonctionne la commission pour les organisateurs ?',
      a: `Pour chaque inscription payante, BoulesConnect prélève une commission de ${COMMISSION_FULL_LABEL} TTC par inscription. Aucun abonnement ni frais fixe : vous ne payez que lorsque vos concours génèrent du chiffre d'affaires. Les concours gratuits sont totalement gratuits (ni commission, ni frais Stripe). Le plan Commission est limité à ${COMMISSION_MONTHLY_LIMIT} concours payants par mois et ${COMMISSION_YEARLY_LIMIT} par an ; au-delà, passez au Forfait.`,
    },
    {
      q: 'Quelle est la différence entre Commission et Forfait ?',
      a: `Le plan Commission est gratuit : vous ne payez que ${COMMISSION_FULL_LABEL} sur les inscriptions payantes, dans la limite de ${COMMISSION_MONTHLY_LIMIT} concours payants par mois (${COMMISSION_YEARLY_LIMIT}/an). Le plan Forfait (${eur(ORGA_FORFAIT_MONTHLY_TTC_CENTS)} € TTC/mois ou ${eur(ORGA_FORFAIT_ANNUAL_TTC_CENTS)} € TTC/an) supprime toute commission et offre des concours payants illimités. Les concours gratuits restent sans frais dans les deux cas.`,
    },
    {
      q: 'Les frais bancaires Stripe sont-ils inclus dans la commission ?',
      a: `Non. Les frais de traitement Stripe sont prélevés par Stripe directement sur le compte de l'organisateur à chaque transaction, en plus de la commission BoulesConnect (${COMMISSION_FULL_LABEL}). Ce sont des frais bancaires standards pour tout paiement en ligne sécurisé.`,
    },
    {
      q: 'Puis-je résilier mon abonnement à tout moment ?',
      a: `Oui. Vous pouvez résilier votre abonnement depuis votre espace personnel. En cas de résiliation, votre abonnement reste actif jusqu'à la fin de la période facturée.`,
    },
    {
      q: "Les tarifs sont-ils susceptibles d'évoluer ?",
      a: `Oui. En cas de hausse de tarif, vous êtes notifié par email au moins 30 jours avant la date d'effet. Vous pouvez résilier sans frais avant l'entrée en vigueur du nouveau tarif ; à défaut, la modification s'applique aux périodes suivantes.`,
    },
  ].map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

export default function Tarifs() {
  const playerPlusEnabled = useSaasStore(s => s.playerPlusEnabled);
  const [billing, setBilling] = useState<Billing>('mensuel');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <PageMeta
        title='Tarifs'
        description={`Tarifs BoulesConnect : gratuit pour les joueurs et les petits clubs, commission de ${COMMISSION_FULL_LABEL} TTC par inscription ou forfait sans commission pour les organisateurs, abonnement Pro pour les clubs structurés.`}
      />
      <JsonLd data={buildSoftwareJsonLd(playerPlusEnabled)} />
      <JsonLd data={faqJsonLd} />

      {/* Hero */}
      <section className='pt-40 pb-16 px-6'>
        <div className='max-w-5xl mx-auto text-center'>
          <div className='inline-flex items-center gap-2 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full mb-6'>
            <Tag className='w-4 h-4 text-blue-600' />
            <span className='text-xs font-bold text-blue-700 uppercase tracking-wider'>Tarifs</span>
          </div>
          <h1 className='text-4xl md:text-5xl font-extrabold text-slate-900 mb-4'>
            Des tarifs simples et transparents
          </h1>
          <p className='text-lg text-slate-500 max-w-2xl mx-auto'>
            Gratuit pour commencer. Vous ne payez que si vous générez des inscriptions payantes ou que vous avez besoin
            de fonctionnalités avancées.
          </p>

          {/* Toggle billing */}
          <div className='mt-8 inline-flex items-center gap-2 bg-white rounded-2xl border border-slate-200 p-1.5 shadow-sm'>
            <button
              onClick={() => setBilling('mensuel')}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition ${billing === 'mensuel' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBilling('annuel')}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition inline-flex items-center gap-2 ${billing === 'annuel' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}
            >
              Annuel
              <span className='text-xs font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full'>
                2 mois offert
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className='px-6 pb-16'>
        <div className='max-w-6xl mx-auto space-y-16'>
          {plans.map(group => (
            <div key={group.audience}>
              <div className='text-center mb-8'>
                <h2 className='text-2xl font-extrabold text-slate-900 mb-2'>{group.audience}</h2>
                <p className='text-sm text-slate-500'>{group.description}</p>
              </div>
              <div
                className={`grid gap-6 ${group.tiers.length === 1 ? 'md:grid-cols-1 max-w-lg mx-auto' : group.tiers.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
              >
                {group.tiers.map(tier => {
                  const isPlayerPlus = group.audience === 'Joueurs' && tier.name === 'Plus';
                  const disabled = isPlayerPlus && !playerPlusEnabled;

                  return (
                    <div
                      key={tier.name}
                      className={`relative bg-white rounded-3xl border p-8 shadow-sm ${disabled ? 'opacity-70' : ''} ${tier.highlighted && !disabled ? 'border-blue-200 ring-1 ring-blue-100' : 'border-slate-100'}`}
                    >
                      <div className='flex items-center gap-3'>
                        <h3 className='text-lg font-extrabold text-slate-900'>{tier.name}</h3>
                        {disabled && (
                          <span className='inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full'>
                            <Clock className='w-3 h-3' />
                            Bientôt disponible
                          </span>
                        )}
                      </div>
                      <p className='text-sm text-slate-500 mt-1'>{tier.tagline}</p>
                      {disabled ? (
                        <div className='mt-6'>
                          <p className='text-sm text-slate-500'>
                            L&apos;offre Joueur Plus sera disponible prochainement.
                          </p>
                        </div>
                      ) : (
                        <div className='mt-6 flex items-end gap-2'>
                          {tier.priceLabel ? (
                            <>
                              <span className='text-5xl font-extrabold text-slate-900'>{tier.priceLabel}</span>
                              <span className='text-slate-500 text-sm'>du montant TTC des inscriptions payées</span>
                            </>
                          ) : (
                            <>
                              <span className='text-5xl font-extrabold text-slate-900'>{tier.price[billing]} €</span>
                              <span className='text-slate-500 text-sm'>
                                {`${billing === 'mensuel' ? '/mois' : '/an'}${tier.price[billing] > 0 ? ' TTC' : ''}`}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                      <ul className='mt-6 space-y-3 text-sm text-slate-600'>
                        {tier.features.map(f => (
                          <li key={f} className='flex items-start gap-2'>
                            <Check className='w-4 h-4 text-emerald-600 mt-0.5 shrink-0' />
                            <span>{f}</span>
                          </li>
                        ))}
                        {tier.notIncluded.map(f => (
                          <li key={f} className='flex items-start gap-2 text-slate-400'>
                            <X className='w-4 h-4 mt-0.5 shrink-0' />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      {disabled ? (
                        <div className='mt-8 w-full rounded-xl px-5 py-3 text-sm font-semibold text-center bg-slate-100 text-slate-400 cursor-not-allowed'>
                          Bientôt disponible
                        </div>
                      ) : (
                        <Link
                          to={ROUTES.REGISTER}
                          className={`mt-8 inline-flex items-center justify-center gap-2 w-full rounded-xl px-5 py-3 text-sm font-semibold transition ${tier.highlighted ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-100 text-slate-900 hover:bg-slate-200'}`}
                        >
                          {tier.cta}
                          <ArrowRight className='w-4 h-4' />
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparatif */}
      {/*<section className='px-6 pb-20'>*/}
      {/*  <div className='max-w-4xl mx-auto bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden'>*/}
      {/*    <div className='p-8 border-b border-slate-100'>*/}
      {/*      <h2 className='text-xl font-extrabold text-slate-900'>*/}
      {/*        Comparatif Joueur Free vs Plus*/}
      {/*        {!playerPlusEnabled && (*/}
      {/*          <span className='ml-3 inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full align-middle'>*/}
      {/*            <Clock className='w-3 h-3' />*/}
      {/*            Bientôt disponible*/}
      {/*          </span>*/}
      {/*        )}*/}
      {/*      </h2>*/}
      {/*    </div>*/}
      {/*    <div className='overflow-x-auto'>*/}
      {/*      <table className='w-full text-sm'>*/}
      {/*        <thead>*/}
      {/*          <tr className='bg-slate-50'>*/}
      {/*            <th className='px-6 py-3 text-left font-bold text-slate-900'>Fonctionnalité</th>*/}
      {/*            <th className='px-6 py-3 text-center font-bold text-slate-900'>Free</th>*/}
      {/*            <th className='px-6 py-3 text-center font-bold text-slate-900'>Plus</th>*/}
      {/*          </tr>*/}
      {/*        </thead>*/}
      {/*        <tbody>*/}
      {/*          {comparatifLignes.map((l, i) => (*/}
      {/*            <tr key={l.feature} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>*/}
      {/*              <td className='px-6 py-3 text-slate-700'>{l.feature}</td>*/}
      {/*              <td className='px-6 py-3 text-center'>*/}
      {/*                {l.free === true ? (*/}
      {/*                  <Check className='w-4 h-4 text-emerald-600 inline' />*/}
      {/*                ) : l.free === false ? (*/}
      {/*                  <X className='w-4 h-4 text-slate-300 inline' />*/}
      {/*                ) : (*/}
      {/*                  <span className='text-xs text-slate-500'>{l.free}</span>*/}
      {/*                )}*/}
      {/*              </td>*/}
      {/*              <td className='px-6 py-3 text-center'>*/}
      {/*                {l.plus === true ? (*/}
      {/*                  <Check className='w-4 h-4 text-emerald-600 inline' />*/}
      {/*                ) : l.plus === false ? (*/}
      {/*                  <X className='w-4 h-4 text-slate-300 inline' />*/}
      {/*                ) : (*/}
      {/*                  <span className='text-xs text-slate-700 font-medium'>{l.plus}</span>*/}
      {/*                )}*/}
      {/*              </td>*/}
      {/*            </tr>*/}
      {/*          ))}*/}
      {/*        </tbody>*/}
      {/*      </table>*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</section>*/}

      {/* FAQ */}
      <section className='px-6 pb-20'>
        <div className='max-w-3xl mx-auto'>
          <h2 className='text-2xl font-extrabold text-slate-900 mb-8 text-center'>Questions fréquentes</h2>
          <div className='space-y-3'>
            {faq.map((item, i) => (
              <div key={i} className='bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden'>
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  className='w-full flex items-center justify-between p-5 text-left'
                >
                  <span className='text-sm font-bold text-slate-900'>{item.q}</span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-500 transition ${openFaqIndex === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaqIndex === i && <div className='px-5 pb-5 text-sm text-slate-600 leading-relaxed'>{item.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA finaux */}
      <section className='px-6 pb-24'>
        <div className='max-w-4xl mx-auto bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-10 text-center text-white'>
          <Calendar className='w-10 h-10 mx-auto mb-4 opacity-80' />
          <h2 className='text-2xl md:text-3xl font-extrabold mb-3'>Prêt à organiser votre prochain concours ?</h2>
          <p className='text-blue-100 mb-6 max-w-xl mx-auto'>
            Créez votre compte gratuitement et lancez votre premier concours en quelques minutes.
          </p>
          <Link
            to={ROUTES.REGISTER}
            className='inline-flex items-center gap-2 bg-white text-blue-700 px-6 py-3 rounded-xl text-sm font-bold hover:bg-blue-50 transition'
          >
            Créer mon compte gratuit
            <ArrowRight className='w-5 h-5' />
          </Link>
        </div>
      </section>
    </>
  );
}
